import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Printer,
  Edit3,
  ArrowLeft,
  Loader2,
  FileText,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  Building,
  Activity,
  Calendar,
  CreditCard,
  Briefcase,
  Clock,
  Check
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from './ViewVendorPayment.module.css';
import api from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import AddVendorPaymentDrawer from './AddVendorPaymentDrawer';
import NotFound from '../Error/NotFound';


const ViewVendorPayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchPayment = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/vendor-payments/${id}`);
      if (response.data.success) {
        setPayment(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching vendor payment:', error);
      toast.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayment();
  }, [id]);

  const getPaymentModeLabel = (mode) => {
    const modes = {
      1: 'Bank Transfer',
      2: 'Cheque',
      3: 'Cash',
      4: 'RTGS/NEFT'
    };
    return modes[mode] || 'Other';
  };

  const getStatusConfig = (status) => {
    const s = status?.toLowerCase() || 'pending';
    if (s === 'paid' || s === 'final') return { label: 'PAID', style: styles.paid, dot: <CheckCircle2 size={12} /> };
    if (s === 'partial') return { label: 'PARTIAL', style: styles.partial, dot: <Activity size={12} /> };
    return { label: 'PENDING', style: styles.pending, dot: <Clock size={12} /> };
  };

  const statusConfig = getStatusConfig(payment?.status);

  if (loading) {
    return (
      <div className={styles['view-payment-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Gathering Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return <NotFound />;
  }


  const po = payment.purchase_order || {};
  const totalPaidSoFar = parseFloat(po.advance_paid_amount || 0) + parseFloat(po.final_paid || 0);
  const outstandingAmt = parseFloat(po.total_amount || 0) - totalPaidSoFar;

  return (
    <div className={`${styles['view-payment-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Payment Detail</h1>
              <p>Review financial transaction and settlement details</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Printer size={18} />
                <span>Print</span>
              </button>
              <button className={styles['btn-secondary']} onClick={() => navigate('/payment/vendor-payments')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Edit Record</span>
              </button>
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['payment-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>#PAY-{payment.id}</h1>
                <span className={`${styles['status-badge']} ${statusConfig.style}`}>
                  {statusConfig.dot}
                  {statusConfig.label}
                </span>
              </div>
            </div>

            <div className={styles['payment-meta']}>
              <div className={styles['meta-item']}>
                <Briefcase size={16} />
                <span>Project: <strong>{payment.project?.project_name}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Building size={16} />
                <span>Vendor: <strong>{payment.vendor?.vendor_name}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Calendar size={16} />
                <span>Payment Date: <strong>{new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
              </div>
            </div>
          </div>

          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-txn']}`}><CheckCircle2 size={20} /></div>
                <span className={styles['m-trend']}>{new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
              </div>
              <div className={styles['m-info']}>
                <label>This Transaction</label>
                <h2 style={{ color: '#10b981' }}>₹{parseFloat(payment.amount).toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-total']}`}><IndianRupee size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>LOI Grand Total</label>
                <h2>₹{parseFloat(po.total_amount || 0).toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-paid']}`}><Briefcase size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Total Paid to Date</label>
                <h2 style={{ color: '#10b981' }}>₹{totalPaidSoFar.toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-balance']}`}><AlertCircle size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Remaining Balance</label>
                <h2 style={{ color: '#f97316' }}>₹{outstandingAmt.toLocaleString('en-IN')}</h2>
              </div>
            </div>
          </div>

          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><CreditCard size={20} /><h3>Transaction Details</h3></div>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Payment Date</span>
                    <span className={styles['d-value']}>{new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Payment Mode</span>
                    <span className={styles['d-value']}>{getPaymentModeLabel(payment.payment_mode)}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Reference / Transaction #</span>
                    <span className={styles['d-value']}>{payment.ref_transaction_number || 'N/A'}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Paid From (Bank Account)</span>
                    <span className={styles['d-value']}>{payment.bank_account?.bank_name || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><FileText size={20} /><h3>Related LOI (Letter of Intent)</h3></div>
                </div>
                <div className={styles['po-preview-box']}>
                  <div className={styles['po-preview-header']}>
                    <div className={styles['po-tag']}>{po.po_number || 'N/A'}</div>
                    <span className={styles['d-label']}>Order Date: {new Date(po.po_date).toLocaleDateString()}</span>
                  </div>
                  <div className={styles['po-details']}>
                    <div className={styles['po-stat']}>
                      <label>Total Value</label>
                      <span>₹{parseFloat(po.total_amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className={styles['po-stat']}>
                      <label>Advance Paid</label>
                      <span>₹{parseFloat(po.advance_paid_amount || 0).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><FileText size={18} /><h3>Remarks / Internal Notes</h3></div>
                </div>
                <p className={styles['note-text']}>{payment.remarks || 'No remarks provided for this transaction.'}</p>
              </div>
            </div>

            <div className={styles['right-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><Building size={20} /><h3>Stakeholders</h3></div>
                </div>
                <div className={styles['stakeholder-list']}>
                  <div className={styles['st-item']}>
                    <div className={styles['st-avatar']}>V</div>
                    <div className={styles['st-info']}>
                      <label>VENDOR</label>
                      <h4>{payment.vendor?.vendor_name}</h4>
                    </div>
                  </div>
                  <div className={styles['st-item']}>
                    <div className={styles['st-avatar']} style={{ background: '#ffedd5', color: '#f97316' }}>P</div>
                    <div className={styles['st-info']}>
                      <label>PROJECT</label>
                      <h4>{payment.project?.project_name}</h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`${styles['standard-card']} ${styles['card-dark']}`}>
                <h3>Settlement Summary</h3>
                <div className={styles['sum-rows']}>
                  <div className={styles['s-row']}><span>LOI Grand Total</span><span>₹{parseFloat(po.total_amount || 0).toLocaleString()}</span></div>
                  <div className={styles['s-row']}><span>Total Paid Before</span><span>₹{(totalPaidSoFar - parseFloat(payment.amount)).toLocaleString()}</span></div>
                  <div className={styles['s-row']}><span>Current Payment</span><span>₹{parseFloat(payment.amount).toLocaleString()}</span></div>
                  <div className={styles['s-divider']}></div>
                  <div className={`${styles['s-row']} ${styles['highlight']}`}><span>Net Paid</span><span>₹{totalPaidSoFar.toLocaleString()}</span></div>
                  <div className={styles['s-divider']}></div>
                  <div className={`${styles['s-row']} ${styles['outstanding']}`}><span>Balance Due</span><span>₹{outstandingAmt.toLocaleString()}</span></div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><Activity size={18} /><h3>Activity Status</h3></div>
                </div>
                <div className={styles['timeline-v']}>
                  <div className={`${styles['tl-item']} ${styles['active']}`}>
                    <div className={styles['tl-dot']}><Check size={10} /></div>
                    <div className={styles['tl-content']}>
                      <h4>Payment Initiated</h4>
                      <p>{new Date(payment.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className={`${styles['tl-item']} ${payment.status === 'paid' || payment.status === 'final' ? styles['active'] : styles['current']}`}>
                    <div className={styles['tl-dot']}>{payment.status === 'paid' || payment.status === 'final' ? <Check size={10} /> : null}</div>
                    <div className={styles['tl-content']}>
                      <h4>Payment Verified</h4>
                      <p>{payment.status === 'paid' || payment.status === 'final' ? 'Verified & Completed' : 'Under Review'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <AddVendorPaymentDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSuccess={fetchPayment}
        payment={payment}
      />
    </div>
  );
};

export default ViewVendorPayment;
